/**
 * @deprecated This component has been moved to @starjack/entities/ticket.
 * Please use TicketsOverviewUI from '@starjack/entities/ticket' instead.
 * This file is kept for backward compatibility and will be removed in a future version.
 *
 * Migration guide:
 * - Replace: import { TicketsOverviewUI } from '@/components/TicketsOverviewUI/TicketsOverviewUI'
 * - With: import { TicketsOverviewUI } from '@starjack/entities/ticket'
 */
import { StarjackTable } from '@/components/Table/v2/Table';
import { FunctionComponent, useMemo, useState } from 'react';
import { Ticket } from '@/models/ticket';
import { LiftOperator } from '@/models/liftoperator';
import {
    getTicketColumns,
    TicketTableColumnId,
} from '@/components/TicketsOverviewUI/ticketColumns';
import { Box, Drawer, SvgIcon, Typography } from '@mui/material';
import {
    getAllIdentificationBlocks,
    getAllTicketDetailsBlocks,
    IdentificationContentBlockId,
    TicketContentBlockId,
} from '@/components/TicketsOverviewUI/ticketDetails';
import { TicketDrawerBlock } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketDrawerBlock';
import { FlexContainer } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@heroicons/react/24/solid/XMarkIcon';

/**
 * @deprecated This type has been moved to @starjack/entities/ticket.
 * Please use TicketsOverviewUIProps from '@starjack/entities/ticket' instead.
 */
type TicketsOverviewUiProps = {
    currentPageTickets: Ticket[];
    ticketsTotal: number;
    ticketsPerPage: number;
    ticketsLoading: boolean;
    error?: unknown;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    hideColumns?: TicketTableColumnId[];
    hideDetailFields?: TicketContentBlockId[];
    hideIdentificationFields?: IdentificationContentBlockId[];
    onRowClick?: (ticket: Ticket) => void;
    liftOperators?: LiftOperator[];
    defaultSortColumn?: string;
};

/**
 * @deprecated This component has been moved to @starjack/entities/ticket.
 * Please use TicketsOverviewUI from '@starjack/entities/ticket' instead.
 */
export const TicketsOverviewUI: FunctionComponent<TicketsOverviewUiProps> = ({
    currentPageTickets = [],
    ticketsTotal,
    ticketsPerPage,
    ticketsLoading,
    error,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    defaultSortColumn,
    hideColumns = [],
    liftOperators = [],
    hideDetailFields = [],
    hideIdentificationFields = [],
}) => {
    const [drawerTicketId, setDrawerTicketId] = useState<string>(null);

    const columnsFilteredByConfig = useMemo(() => {
        const columns = getTicketColumns({ liftOperators });

        return columns.filter(
            (column) =>
                !hideColumns.find(
                    (hideColumn) => hideColumn === column.columnId
                )
        );
    }, [hideColumns, liftOperators]);

    const drawerTicket =
        currentPageTickets.find((cT) => cT.id === drawerTicketId) || null;

    const ticketDrawerBlocks = useMemo(() => {
        if (!drawerTicketId || !drawerTicket) return [];

        const identificationBlocks = drawerTicket?.identifications.map(
            (identification) =>
                getAllIdentificationBlocks(identification).filter(
                    (iB) => !hideIdentificationFields.includes(iB.blockId)
                )
        );

        const liftOperator = liftOperators.find(
            (l) => l.id === drawerTicket?.liftOperatorId
        );

        return getAllTicketDetailsBlocks({
            ticket: drawerTicket,
            identifications: identificationBlocks,
            liftOperator,
        });
    }, [
        drawerTicketId,
        hideDetailFields,
        hideIdentificationFields,
        liftOperators,
    ]);

    return (
        <>
            <StarjackTable<Ticket>
                itemId={'id'}
                items={currentPageTickets}
                columns={columnsFilteredByConfig}
                count={ticketsTotal}
                rowsPerPage={ticketsPerPage}
                loading={ticketsLoading}
                error={error}
                onPageChange={onPageChange}
                onRowsPerPageChange={onPageSizeChange}
                onRowClick={(item) => {
                    setDrawerTicketId(item.id);

                    onRowClick?.(item);
                }}
                rowsSelectable={false}
                defaultSortColumn={defaultSortColumn || 'orderDate'}
                selectRow={(ticket) => ticket.id === drawerTicket?.id}
            />

            <Drawer
                anchor='right'
                open={!!drawerTicketId}
                onClose={() => {
                    setDrawerTicketId(null);
                }}
                variant='persistent'
            >
                <Box
                    sx={{
                        width: '580px',
                    }}
                >
                    <FlexContainer
                        sx={(theme) => ({
                            height: '72px',
                            position: 'fixed',
                            width: '580px',
                            alignItems: 'center',
                            backgroundColor: theme.palette.primary.dark,
                            color: '#fff',
                            p: 4,
                        })}
                    >
                        <Typography variant='h6'>
                            {drawerTicket?.name}
                        </Typography>

                        <IconButton
                            aria-label='Schließen'
                            sx={{
                                ml: 'auto',
                            }}
                            onClick={() => {
                                setDrawerTicketId(null);
                            }}
                        >
                            <SvgIcon
                                sx={{
                                    color: '#fff',
                                    fontSize: '28px',
                                }}
                            >
                                <CloseIcon />
                            </SvgIcon>
                        </IconButton>
                    </FlexContainer>

                    <Box
                        sx={{
                            mt: '72px',
                        }}
                    >
                        {ticketDrawerBlocks.map((block) => (
                            <TicketDrawerBlock
                                key={block.blockId}
                                header={block.header}
                            >
                                {block.content}
                            </TicketDrawerBlock>
                        ))}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
